/* Benchmark email capture popup.

   Replaces the Klaviyo "Join the club" popup (form UDAVbs) and keeps its
   behaviour: show after a delay or a scroll depth, whichever comes first, on a
   whitelist of pages, on the configured device class, and not again for a set
   number of days. The email goes to the same Klaviyo list the old form used,
   through the public client subscription endpoint, so the welcome flow that
   hangs off that list keeps firing. */

(() => {
  const TAG = 'email-popup-benchmark';

  // the Klaviyo form this popup replaced was never switched off in Klaviyo,
  // so it kept firing underneath. Close it through Klaviyo's onsite API the
  // moment it opens - this runs regardless of whether the component below can
  // register, because the stacking bug is worse than a missing popup.
  const suppressKlaviyoForm = () => {
    const raw = document.querySelector('[data-email-popup-settings]')?.textContent;
    let formId = '';
    try {
      formId = JSON.parse(raw || '{}').suppress_form || '';
    } catch (error) {
      formId = '';
    }
    if (!formId) return;
    window.addEventListener('klaviyoForms', (e) => {
      if (e.detail?.formId !== formId) return;
      if (e.detail.type === 'open' || e.detail.type === 'embedOpen') {
        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push(['closeForm', formId]);
      }
    });
  };
  suppressKlaviyoForm();

  if (customElements.get(TAG)) return;
  // the component extends ModalDialog from global.js; if that never loaded,
  // failing silently is better than throwing and taking the page down with it
  if (typeof ModalDialog === 'undefined') return;

  const SEEN_KEY = 'bm-email-popup-seen';
  const DONE_KEY = 'bm-email-popup-subscribed';
  const KLAVIYO_REVISION = '2024-10-15';
  const DAY = 86400000;
  const CSS_ID = 'EmailPopupBenchmarkCSS';
  // a trigger that lands while another layer is up looks again this much
  // later, this many times, then gives up for the page view
  const RETRY_MS = 8000;
  const MAX_RETRIES = 10;
  // the open-layer selectors BMScrollLock.sanitize uses, plus the immersive
  // reels on /pages/app (their hide rule misses this sheet once ModalDialog
  // has moved it to <body>)
  const OPEN_LAYERS =
    'cart-drawer.active, cart-drawer.is-closing, [data-fm-modal]:not([hidden]), .header-benchmark__drawer[open], .hiw-reels--immersive';

  // storage throws in private-mode Safari and when cookies are blocked, and a
  // popup is never worth breaking a page over
  const safeGet = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  const safeSet = (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      /* ignore */
    }
  };

  const safeRemove = (key) => {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      /* ignore */
    }
  };

  const parseJSON = (node) => {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  };

  // "/products/golf-at-home*" style rules, one per line, matched against the
  // path only. a bare "/" is the home page and nothing else.
  const parsePageRules = (raw) =>
    String(raw || '')
      .split(/[\n,]+/)
      .map((entry) => entry.trim())
      .filter(Boolean);

  const normalisePath = (path) => {
    const value = String(path || '/').split('?')[0].split('#')[0];
    if (value.length > 1 && value.endsWith('/')) return value.slice(0, -1);
    return value;
  };

  const ruleMatches = (rule, path) => {
    const target = normalisePath(rule.replace(/^https?:\/\/[^/]+/, ''));
    if (!target.includes('*')) return target === path;
    const pattern = target
      .split('*')
      .map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('.*');
    return new RegExp(`^${pattern}$`).test(path);
  };

  const pagesMatch = (rules, path) => {
    // no rules configured means every page, which is how the theme editor
    // reads an empty textarea
    if (!rules.length) return true;
    const current = normalisePath(path);
    return rules.some((rule) => ruleMatches(rule, current));
  };

  const isMobileViewport = () => window.matchMedia('(max-width: 749px)').matches;

  const devicesMatch = (devices) => {
    if (devices === 'all') return true;
    if (devices === 'mobile') return isMobileViewport();
    return !isMobileViewport();
  };

  const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || '').trim());

  // is the visitor already looking at another overlay: the cart drawer, the
  // Founding Members sheet, the nav panel, a Judge.me or other ModalDialog
  // modal. The 45s timer keeps running under all of them, and this sheet's
  // z-index (1001) beats the drawer (1000) and the fm-modal (60). A lock that
  // leaked open only ever delays or skips the popup.
  const layerOpen = () => {
    const body = document.body;
    if (document.documentElement.dataset.bmScrollLock) return true;
    if (body.classList.contains('modal-dialog-open')) return true;
    if (body.style.overflow === 'hidden' || body.style.position === 'fixed') return true;
    return Boolean(document.querySelector(OPEN_LAYERS));
  };

  // has the popup stylesheet loaded (or is there no link to wait for).
  // Firefox sets link.sheet while the file is still in flight and throws
  // InvalidAccessError on cssRules until it lands; a cross-origin sheet
  // throws SecurityError only once it has loaded.
  const cssLoaded = () => {
    const link = document.getElementById(CSS_ID);
    if (!link) return true;
    if (!link.sheet) return false;
    try {
      return Boolean(link.sheet.cssRules);
    } catch (error) {
      return error.name === 'SecurityError';
    }
  };

  const scrollPercentage = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return 0;
    return (window.scrollY / scrollable) * 100;
  };

  class EmailPopupBenchmark extends ModalDialog {
    constructor() {
      super();

      this.settings = parseJSON(this.querySelector('[data-email-popup-settings]'));
      this.messages = parseJSON(this.querySelector('[data-email-popup-messages]')) || {};
      this.form = this.querySelector('[data-email-popup-form]');
      this.copy = this.querySelector('[data-email-popup-copy]');
      this.input = this.querySelector('input[type="email"]');
      this.submit = this.querySelector('[data-email-popup-submit]');
      this.errorNode = this.querySelector('[data-email-popup-error]');
      this.successNode = this.querySelector('[data-email-popup-success]');

      this.hide = this.hide.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      this.form?.addEventListener('submit', this.onSubmit);
      this.input?.addEventListener('input', () => this.clearError());
      // the cart drawer sits above this sheet, so never stack the two. A cart
      // open also mutes the popup for the rest of the page view: that visitor
      // is mid-purchase, and the pending timer would otherwise open the sheet
      // on top of the drawer or right after it closes.
      document.addEventListener('cart-drawer:show', () => {
        this.suppressed = true;
        this.disarm();
        window.clearTimeout(this.retry);
        this.hide();
      });
    }

    connectedCallback() {
      super.connectedCallback();
      if (this.evaluated) return;
      this.evaluated = true;
      this.evaluate();
    }

    evaluate() {
      const settings = this.settings;
      if (!settings) return;

      // the force parameter is the preview path: it opens the sheet whatever
      // the gates say and never writes the seen flag, so it can be reused
      if (settings.force_param && new URLSearchParams(window.location.search).has(settings.force_param)) {
        this.forced = true;
        safeRemove(SEEN_KEY);
        this.useStylesAtAnyWidth();
        this.open();
        return;
      }

      if (!settings.enabled) return;
      if (safeGet(DONE_KEY) === '1') return;
      if (!devicesMatch(settings.devices)) return;
      if (!pagesMatch(parsePageRules(settings.page_rules), window.location.pathname)) return;
      if (this.seenRecently()) return;

      this.arm();
    }

    seenRecently() {
      const days = Number(this.settings.repeat_days) || 0;
      if (days <= 0) return false;
      const seen = Number(safeGet(SEEN_KEY));
      if (!seen) return false;
      return Date.now() - seen < days * DAY;
    }

    // delay and scroll depth race each other, exactly as the klaviyo trigger
    // group did: whichever lands first opens the sheet
    arm() {
      const delay = Math.max(0, Number(this.settings.delay) || 0) * 1000;
      this.timer = window.setTimeout(() => this.open(), delay);

      const threshold = Number(this.settings.scroll) || 0;
      if (threshold > 0) {
        this.scrollThreshold = threshold;
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
    }

    disarm() {
      window.clearTimeout(this.timer);
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      if (scrollPercentage() < this.scrollThreshold) return;
      this.open();
    }

    // the preview skips every gate. A real trigger never opens over another
    // layer. Neither opens before the stylesheet has applied - the inline
    // pre-hide would win, and ModalDialog would lock the page and move focus
    // into a sheet nobody can see.
    async open() {
      if (this.opened || (this.suppressed && !this.forced)) return;
      if (this.forced) {
        this.opened = true;
        this.disarm();
        // a loaded sheet keeps the preview synchronous, as it always was: a
        // matching link holds this deferred script back until it loads. Only
        // a preview below 750px of a desktop-only popup can arrive before its
        // sheet, because that non-matching link was fetched at lowest priority.
        if (!cssLoaded() && !(await this.cssReady())) return;
        this.show();
        return;
      }
      if (layerOpen()) {
        this.deferOpen();
        return;
      }
      this.opened = true;
      this.disarm();
      if (!(await this.cssReady())) return;
      // a layer can open while the stylesheet loads
      if (this.suppressed || layerOpen()) {
        if (!this.suppressed) {
          this.opened = false;
          this.deferOpen();
        }
        return;
      }
      this.show();
    }

    // another layer is up: look again in RETRY_MS, at most MAX_RETRIES
    // times, then give up for this page view without writing the seen flag
    deferOpen() {
      this.disarm();
      window.clearTimeout(this.retry);
      this.retries = (this.retries || 0) + 1;
      if (this.retries > MAX_RETRIES) return;
      this.retry = window.setTimeout(() => this.open(), RETRY_MS);
    }

    // a desktop-only popup links its stylesheet with media="(min-width:
    // 750px)" so phones never block on it. Anything that opens the sheet
    // outside that media (the preview on a phone, a desktop window narrowed
    // below 750px) switches it to all first.
    useStylesAtAnyWidth() {
      const link = document.getElementById(CSS_ID);
      if (link && link.media !== 'all') link.media = 'all';
    }

    // resolves true once the popup stylesheet has loaded, false if it errors
    // or is still missing after 3s
    cssReady() {
      this.useStylesAtAnyWidth();
      if (cssLoaded()) return Promise.resolve(true);
      const link = document.getElementById(CSS_ID);
      return new Promise((resolve) => {
        let timer = 0;
        const done = (ok) => {
          window.clearTimeout(timer);
          link.removeEventListener('load', onLoad);
          link.removeEventListener('error', onError);
          resolve(ok);
        };
        const onLoad = () => done(true);
        const onError = () => done(false);
        timer = window.setTimeout(() => done(cssLoaded()), 3000);
        link.addEventListener('load', onLoad);
        link.addEventListener('error', onError);
      });
    }

    markSeen() {
      // a preview open must leave storage untouched, otherwise reviewing the
      // popup silently suppresses it for the next real visit
      if (this.forced) return;
      safeSet(SEEN_KEY, String(Date.now()));
    }

    clearError() {
      if (!this.errorNode) return;
      this.errorNode.hidden = true;
      this.errorNode.textContent = '';
    }

    showError(message) {
      if (!this.errorNode) return;
      this.errorNode.textContent = message;
      this.errorNode.hidden = false;
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.pending) return;

      const email = this.input?.value.trim();
      if (!isEmail(email)) {
        this.showError(this.messages.invalid || 'Enter a valid email address.');
        this.input?.focus();
        return;
      }

      this.clearError();
      this.pending = true;
      if (this.submit) this.submit.disabled = true;

      try {
        await this.subscribe(email);
        this.onSubscribed();
      } catch (error) {
        this.showError(this.messages.failed || 'That did not go through. Please try again.');
      } finally {
        this.pending = false;
        if (this.submit) this.submit.disabled = false;
      }
    }

    // klaviyo's public client endpoint: company id and list id are both public
    // values, no private key ever reaches the storefront
    async subscribe(email) {
      const { company_id: companyId, list_id: listId } = this.settings;
      if (!companyId || !listId) throw new Error('email popup is missing its klaviyo ids');

      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            revision: KLAVIYO_REVISION,
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Benchmark email popup',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email },
                  },
                },
              },
              relationships: {
                list: { data: { type: 'list', id: listId } },
              },
            },
          }),
        }
      );

      if (!response.ok) throw new Error(`klaviyo subscribe failed: ${response.status}`);
    }

    onSubscribed() {
      safeSet(DONE_KEY, '1');
      // the success block carries its own heading, so the pitch above the form
      // goes with the form rather than stacking two headings on the card
      this.copy?.setAttribute('hidden', '');
      this.form?.setAttribute('hidden', '');
      if (this.successNode) this.successNode.hidden = false;
      window.dataLayer?.push({ event: 'benchmark_email_popup_subscribed' });
      document.dispatchEvent(new CustomEvent('email-popup:subscribed'));
    }

    // ModalDialog.hide strips the shared body scroll-lock classes with no
    // already-closed guard, so an unguarded call here would unlock the page the
    // moment the cart drawer opened and added them for itself
    hide() {
      if (!this.hasAttribute('open')) return;
      super.hide();
    }

    show(opener) {
      super.show(opener);
      this.markSeen();
      window.dataLayer?.push({ event: 'benchmark_email_popup_shown' });
      document.dispatchEvent(new CustomEvent('email-popup:shown'));
    }
  }

  customElements.define(TAG, EmailPopupBenchmark);
})();
